// src/components/opportunity/OpportunityPricingTab.tsx
//
// Thin dispatcher. Reads the opportunity's product_type, asks the
// format registry which form to render, and hands the shared form state
// + save panel down to the chosen form. The format-specific forms
// (`OpportunityPricingFormRedIron`, `OpportunityPricingFormGeneric`)
// own the field layouts; this tab only owns the state and the save
// flow.

import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import type {
  Opportunity,
  OpportunityPricingSnapshot,
} from "@/types/opportunity";
import { saveOpportunityRevision } from "@/lib/opportunity/opportunityClient";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useOpportunityFormatPreset } from "@/hooks/useOpportunityFormatPreset";
import {
  emptyOpportunityPricingFormState,
  formFromSnapshot,
  pricingFormFingerprint,
  pricingInputsFromForm,
  type OpportunityPricingFormState,
} from "@/components/opportunity/OpportunityPricingForm";
import { resolveManagerPresetFormatKey, resolveOpportunityFormatKey } from "@/lib/opportunity/formatRegistry";
import { snapshotFormatKey } from "@/types/opportunity";
import {
  validateFormatCalcOverrides,
  validateSalesPricingInputs,
} from "@/lib/opportunity/salesAdjustmentsValidation";
import { OpportunityPricingFormRedIron } from "@/components/opportunity/OpportunityPricingFormRedIron";
import { OpportunityPricingFormGeneric } from "@/components/opportunity/OpportunityPricingFormGeneric";
import { OpportunityProposalContactPanel } from "@/components/opportunity/OpportunityProposalContactPanel";
import { OpportunityOfferingGuidancePanel } from "@/components/opportunity/OpportunityOfferingGuidancePanel";
import { useOpportunityServiceOfferingMatch } from "@/components/opportunity/useOpportunityServiceOfferingMatch";

export type OpportunityPricingHandle = {
  isDirty: () => boolean;
  saveIfDirty: () => Promise<{ ok: true } | { ok: false; error: string }>;
};

export type OpportunityPricingTabProps = {
  opportunity: Opportunity;
  activeSnapshot: OpportunityPricingSnapshot | null;
  onSaved: () => void;
  onOpenDocumentsTab?: () => void;
  pricingHandleRef?: Ref<OpportunityPricingHandle | null>;
};

export function OpportunityPricingTab(props: OpportunityPricingTabProps) {
  const { opportunity, activeSnapshot, onSaved, onOpenDocumentsTab, pricingHandleRef } = props;
  const org = useOrgSettings();
  const { matchedOffering } = useOpportunityServiceOfferingMatch(opportunity.product_type);
  const legacyRedIronSnapshot =
    activeSnapshot != null && snapshotFormatKey(activeSnapshot) === "red_iron";
  const formatKey = legacyRedIronSnapshot ? "red_iron" : resolveOpportunityFormatKey(opportunity.product_type);
  const presetFormatKey = resolveManagerPresetFormatKey(opportunity.product_type);
  const { calcLines, depositRule, preset, loading: presetLoading, error: presetError } =
    useOpportunityFormatPreset({
      formatKey: presetFormatKey,
      productType: opportunity.product_type,
    });

  const [form, setForm] = useState<OpportunityPricingFormState>(() =>
    activeSnapshot
      ? formFromSnapshot(activeSnapshot)
      : emptyOpportunityPricingFormState({
          formatKey,
          commissionPct: org.defaultCommissionPct,
          marginPct: org.defaultMarginPct,
        }),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const presetCalcSeededRef = useRef<string | null>(null);
  const depositSeedFingerprintRef = useRef<string | null>(null);
  const baselineFingerprintRef = useRef<string>(
    pricingFormFingerprint(
      activeSnapshot
        ? formFromSnapshot(activeSnapshot)
        : emptyOpportunityPricingFormState({
            formatKey,
            commissionPct: org.defaultCommissionPct,
            marginPct: org.defaultMarginPct,
          }),
    ),
  );
  const formRef = useRef(form);
  formRef.current = form;
  const activeSnapshotId = activeSnapshot?.id ?? null;

  useEffect(() => {
    presetCalcSeededRef.current = null;
    depositSeedFingerprintRef.current = null;
  }, [opportunity.id]);

  // Re-seed from the active snapshot when its identity changes (new
  // revision). Keeps the form aligned after Save revision / auto-save
  // without wiping in-progress edits on same-snapshot refreshes.
  useEffect(() => {
    if (!activeSnapshot) return;
    const next = formFromSnapshot(activeSnapshot);
    setForm(next);
    baselineFingerprintRef.current = pricingFormFingerprint(next);
    setSavedAt(null);
    setSaveError(null);
  }, [activeSnapshotId]); // eslint-disable-line react-hooks/exhaustive-deps -- identity only

  useEffect(() => {
    if (activeSnapshot || presetLoading || legacyRedIronSnapshot) return;
    if (calcLines.length === 0) return;
    const key = `${opportunity.id}:calc`;
    if (presetCalcSeededRef.current === key) return;
    presetCalcSeededRef.current = key;
    setForm((prev) => {
      const formatCalcValues = { ...prev.formatCalcValues };
      for (const line of calcLines) {
        if (!(line.id in formatCalcValues)) {
          formatCalcValues[line.id] = line.default_amount
            ? String(line.default_amount)
            : "";
        }
      }
      return { ...prev, formatCalcValues };
    });
  }, [activeSnapshot, presetLoading, legacyRedIronSnapshot, calcLines, opportunity.id]);

  useEffect(() => {
    if (activeSnapshot || presetLoading || legacyRedIronSnapshot) return;
    const fingerprint = `${depositRule.deposit_mode}:${depositRule.deposit_value}`;
    if (depositSeedFingerprintRef.current === fingerprint) return;
    depositSeedFingerprintRef.current = fingerprint;
    setForm((prev) => ({
      ...prev,
      depositMode: depositRule.deposit_mode === "amount" ? "amount" : "percent",
      depositPct:
        depositRule.deposit_mode === "percent"
          ? String(depositRule.deposit_value)
          : prev.depositPct,
      depositAmountInput:
        depositRule.deposit_mode === "amount"
          ? String(depositRule.deposit_value)
          : prev.depositAmountInput,
    }));
  }, [
    activeSnapshot,
    presetLoading,
    legacyRedIronSnapshot,
    depositRule,
    opportunity.id,
  ]);

  // When the org defaults arrive after first render (initial fetch
  // resolves) and the form is still completely empty (no snapshot, no
  // user edits), apply them. We never overwrite a value the user has
  // typed.
  useEffect(() => {
    if (activeSnapshot) return;
    setForm((prev) => {
      const next = { ...prev };
      if (!prev.commissionPct && org.defaultCommissionPct != null) {
        next.commissionPct = String(org.defaultCommissionPct);
      }
      if (!prev.marginPct && org.defaultMarginPct != null) {
        next.marginPct = String(org.defaultMarginPct);
      }
      return next;
    });
  }, [activeSnapshot, org.defaultCommissionPct, org.defaultMarginPct]);

  useEffect(() => {
    if (legacyRedIronSnapshot) return;
    setForm((prev) => (prev.formatKey === "generic" ? prev : { ...prev, formatKey: "generic" }));
  }, [legacyRedIronSnapshot, opportunity.product_type]);

  async function persistRevision(opts?: {
    notifySaved?: boolean;
  }): Promise<{ ok: true } | { ok: false; error: string }> {
    const current = formRef.current;
    const currentInputs = pricingInputsFromForm(current);
    const validationError =
      validateSalesPricingInputs(currentInputs) ??
      validateFormatCalcOverrides(currentInputs, calcLines);
    if (validationError) {
      setSaveError(validationError);
      return { ok: false, error: validationError };
    }
    const r = await saveOpportunityRevision({
      opportunity_id: opportunity.id,
      inputs: currentInputs,
      notes: current.notes.trim() || undefined,
    });
    if (!r.ok) {
      setSaveError(r.error);
      return { ok: false, error: r.error };
    }
    baselineFingerprintRef.current = pricingFormFingerprint(current);
    setSavedAt(Date.now());
    setSaveError(null);
    if (opts?.notifySaved !== false) onSaved();
    return { ok: true };
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    await persistRevision();
    setSaving(false);
  }

  useImperativeHandle(
    pricingHandleRef,
    () => ({
      isDirty: () =>
        pricingFormFingerprint(formRef.current) !== baselineFingerprintRef.current,
      saveIfDirty: async () => {
        if (pricingFormFingerprint(formRef.current) === baselineFingerprintRef.current) {
          return { ok: true };
        }
        setSaving(true);
        setSaveError(null);
        const result = await persistRevision({ notifySaved: false });
        setSaving(false);
        return result;
      },
    }),
    [opportunity.id, onSaved, calcLines],
  );

  function renderSavePanel() {
    return (
      <>
        {saveError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
            {saveError}
          </div>
        ) : null}
        {savedAt && !saveError ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100">
            Revision saved.
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="w-full px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Saving revision…" : "Save revision"}
        </button>
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          Saving creates an immutable snapshot and bumps the revision number.
          Preview / Save proposal also save unsaved pricing edits first.
        </p>
      </>
    );
  }

  if (legacyRedIronSnapshot) {
    return (
      <div className="space-y-4">
        <OpportunityProposalContactPanel opportunity={opportunity} onChanged={onSaved} />
        <OpportunityOfferingGuidancePanel
          matchedOffering={matchedOffering}
          onOpenDocumentsTab={onOpenDocumentsTab}
        />
        <OpportunityPricingFormRedIron
          opportunity={opportunity}
          form={form}
          setForm={setForm}
          renderSavePanel={renderSavePanel}
        />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <OpportunityProposalContactPanel opportunity={opportunity} onChanged={onSaved} />
      <OpportunityOfferingGuidancePanel
        matchedOffering={matchedOffering}
        onOpenDocumentsTab={onOpenDocumentsTab}
      />
      <OpportunityPricingFormGeneric
      form={form}
      setForm={setForm}
      calcLines={calcLines}
      formatPreset={preset}
      presetLoading={presetLoading}
      presetError={presetError}
      renderSavePanel={renderSavePanel}
    />
    </div>
  );
}
